import { InformationCircleLight } from '@ha/icons';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, FormSpy, Field } from 'react-final-form';
import { makeStyles } from 'tss-react/mui';

import {
  Box,
  Typography,
  Grid,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  Tooltip,
  TextField,
  Divider,
} from '@hbf/dsl';
import { useIntl } from 'ha/i18n';
import { useMediaQuery } from 'ha/helpers/MediaQuery';
import { DialogNext as Dialog } from 'ha/components/Dialog';
import { Condition } from 'ha/components.legacy/FormFields/Condition';
import { reportReasons as REPORT_REASONS } from 'ha/constants/ReportReasons';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';
import { useAuthModalActions } from 'ha/modules/NavBar/hooks/useAuthModalActions';
import { getIsAuthenticated } from 'ha/modules/AuthLogic/selectors';

import { reportUser } from './actions';

interface FormValues {
  reason: string;
  message: string;
}

interface Props {
  reportedUserID: number;
  buttonElement: React.FC<{ onClick: (evt: React.MouseEvent) => void }>;
}

const useStyles = makeStyles()(t => ({
  divider: {
    marginTop: t.spacing(3),
  },
}));

const ReportUserBtn: React.FC<Props> = ({ reportedUserID, buttonElement }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { T } = useIntl();
  const { sm } = useMediaQuery();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const { onLoginClick } = useAuthModalActions();

  const { classes } = useStyles();
  const [showCloseModal, setShowCloseModal] = React.useState(false);

  const onClick = (evt: React.MouseEvent) => {
    evt.preventDefault();
    if (isAuthenticated) {
      setIsOpen(!isOpen);
    } else {
      onLoginClick();
    }
  };

  const onClose = () => {
    setIsOpen(false);
  };

  const onSubmit = (values: FormValues) => {
    onClose();
    dispatch(
      reportUser(reportedUserID, {
        onSuccess: () => setShowCloseModal(true),
        reason:
          values.reason === REPORT_REASONS.other.value
            ? values.message || values.reason
            : values.reason,
      }),
    );
  };

  const validateForm = (values: FormValues) => {
    const errors: { reason?: string; message?: string } = {};

    if (!values.reason) {
      errors.reason = T('Please select a reason');
    }

    return errors;
  };

  return (
    <React.Fragment>
      {buttonElement({ onClick })}
      <Dialog
        show={showCloseModal}
        onClose={() => setShowCloseModal(false)}
        title={T('tenant_reviews.report_tenant_reviews.thanks')}
        content={
          <Grid container alignItems="center" justifyContent="space-between">
            <Typography.Paragraph>
              {T('report_advertiser.thanks_body')}
            </Typography.Paragraph>
            <Divider className={classes.divider} />
          </Grid>
        }
        dialogActions={
          <Button
            variant="contained"
            color="primary"
            type="submit"
            onClick={() => setShowCloseModal(false)}
          >
            {T('cta.close')}
          </Button>
        }
      />
      <Form onSubmit={onSubmit} validate={validateForm}>
        {({ handleSubmit, values, form }) => (
          <Dialog
            show={isOpen}
            onClose={onClose}
            dividers
            maxWidth="sm"
            fullScreen={!sm}
            title={T('Report this user')}
            content={
              <Grid container spacing={3}>
                <Grid item>
                  <Typography.Paragraph>
                    {T(
                      'Please choose one of the following reasons and our dedicated Trust and Safety team will look into it.',
                    )}
                  </Typography.Paragraph>
                </Grid>
                <Grid container item xs={12}>
                  <Box width={1}>
                    <form
                      onSubmit={event => {
                        handleSubmit(event);
                        form.reset();
                      }}
                      id="reportUserForm"
                    >
                      <Field
                        name="reason"
                        type="radio"
                        required
                        value={values.reason}
                      >
                        {({ input }) => (
                          <RadioGroup
                            onChange={input.onChange}
                            name={input.name}
                            value={input.value}
                          >
                            {Object.keys(REPORT_REASONS).map(
                              (reason: string) => (
                                <Grid
                                  container
                                  item
                                  key={REPORT_REASONS[reason].title}
                                >
                                  <Grid item xs={11}>
                                    <FormControlLabel
                                      control={<Radio />}
                                      label={T(REPORT_REASONS[reason].title)}
                                      value={REPORT_REASONS[reason].value}
                                    />
                                  </Grid>
                                  <Grid item xs={1}>
                                    <Box
                                      display="flex"
                                      alignItems="center"
                                      justifyContent="flex-end"
                                      height={1}
                                    >
                                      <Tooltip
                                        title={T(REPORT_REASONS[reason].hint)}
                                        placement="top"
                                      >
                                        <span>
                                          <InformationCircleLight fontSize="small" />
                                        </span>
                                      </Tooltip>
                                    </Box>
                                  </Grid>
                                </Grid>
                              ),
                            )}
                          </RadioGroup>
                        )}
                      </Field>
                      <Condition when="reason" is={REPORT_REASONS.other.value}>
                        <Field name="message">
                          {({ input }) => (
                            <TextField
                              multiline
                              fullWidth
                              variant="filled"
                              rows={4}
                              name={input.name}
                              value={input.value}
                              onChange={input.onChange}
                              placeholder={T('Please describe the reason')}
                            />
                          )}
                        </Field>
                      </Condition>
                    </form>
                  </Box>
                </Grid>
              </Grid>
            }
            dialogActions={
              <FormSpy>
                {({ submitting, invalid }) => (
                  <Track name="Clicked Report user" type="button">
                    <Button
                      variant="contained"
                      color="primary"
                      disabled={submitting || invalid}
                      data-test-locator="Submit Report User Btn"
                      type="submit"
                      form="reportUserForm"
                    >
                      {T('Submit')}
                    </Button>
                  </Track>
                )}
              </FormSpy>
            }
          />
        )}
      </Form>
    </React.Fragment>
  );
};

export { ReportUserBtn };
